import type {
  CaptureSessionState,
  CropCorners,
  ExifNormalizationInput,
  PreparePayload,
  QualityAssessment,
  RawCaptureFile,
} from './types.ts';
import { orderCorners, validateConvexCorners } from './geometry.ts';

const MAX_BYTES = 12 * 1024 * 1024; // 12 MiB per Section 5.1
// TN-02/12: tablet cameras produce HEIC/HEIF. The server decides by content;
// the client admission here only prevents obviously unsupported picks early.
const ALLOWED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
]);
// Browsers that cannot recognise HEIC may report an empty or generic MIME type.
// Admission is delegated to the server-side content sniffing in that case.
const HEIC_ADMISSION_SUBSTITUTES = new Set(['', 'application/octet-stream']);

/**
 * Admits HEIC/HEIF picks whose reported MIME type is empty or generic, by
 * falling back to the filename extension. Extension checking is only an
 * admission hint here — the server-side decoder is authoritative (TN-12).
 */
function isAdmissibleMime(mimeType: string, fileName: string): boolean {
  const normalized = (mimeType || '').toLowerCase();
  if (ALLOWED_MIME_TYPES.has(normalized)) return true;
  if (!HEIC_ADMISSION_SUBSTITUTES.has(normalized)) return false;
  const ext = fileName.toLowerCase().split('.').pop() ?? '';
  return ext === 'heic' || ext === 'heif';
}

export class CaptureManager {
  private state: CaptureSessionState = {
    file: null,
    exifApplied: false,
    rawDimensions: { width: 0, height: 0 },
    previewDimensions: { width: 0, height: 0 },
    rotation: 0,
    cropCorners: [
      [0, 0],
      [0, 0],
      [0, 0],
      [0, 0],
    ],
    isCropAdjusted: false,
    qualityAssessment: {
      status: 'clean',
      calibrated: false,
      metrics: {},
      reasons: [],
      isTechnicalLimit: false,
    },
    effectiveStatus: 'clean',
    overrideAudit: null,
  };

  /**
   * Validate file format and size bounds before ingestion.
   */
  public admitFile(file: RawCaptureFile): { valid: boolean; error?: string } {
    if (file.size > MAX_BYTES) {
      return {
        valid: false,
        error: `File size (${(file.size / (1024 * 1024)).toFixed(1)} MiB) exceeds 12 MiB limit`,
      };
    }

    if (!isAdmissibleMime(file.type, file.name)) {
      return {
        valid: false,
        error: `Unsupported image format (${file.type || 'bilinmeyen'}). JPEG, PNG, WebP ve HEIC/HEIF kabul edilir.`,
      };
    }

    return { valid: true };
  }

  /**
   * Initialize a new capture session with an admitted file.
   */
  public initSession(file: RawCaptureFile): void {
    const check = this.admitFile(file);
    if (!check.valid) {
      throw new Error(check.error);
    }

    this.state = {
      file,
      exifApplied: false,
      rawDimensions: { width: 0, height: 0 },
      previewDimensions: { width: 0, height: 0 },
      rotation: 0,
      cropCorners: [
        [0, 0],
        [0, 0],
        [0, 0],
        [0, 0],
      ],
      isCropAdjusted: false,
      qualityAssessment: {
        status: 'clean',
        calibrated: false,
        metrics: {},
        reasons: [],
        isTechnicalLimit: false,
      },
      effectiveStatus: 'clean',
      overrideAudit: null,
    };
  }

  /**
   * Apply EXIF orientation normalization and set default proposed page crop bounds.
   */
  public applyExifNormalization(input: ExifNormalizationInput): void {
    const { rawWidth, rawHeight, orientation = 1 } = input;
    const isTransposed = [5, 6, 7, 8].includes(orientation);

    const previewWidth = isTransposed ? rawHeight : rawWidth;
    const previewHeight = isTransposed ? rawWidth : rawHeight;

    const defaultCorners: CropCorners = [
      [0, 0],
      [previewWidth - 1, 0],
      [previewWidth - 1, previewHeight - 1],
      [0, previewHeight - 1],
    ];

    this.state.rawDimensions = { width: rawWidth, height: rawHeight };
    this.state.previewDimensions = { width: previewWidth, height: previewHeight };
    this.state.exifApplied = orientation !== 1;
    this.state.cropCorners = defaultCorners;
    this.state.isCropAdjusted = false;
  }

  /**
   * Ingest diagnostic quality assessment.
   * Strictly enforces calibrated = false invariant (§9.6).
   */
  public setQualityAssessment(qa: QualityAssessment): void {
    if (qa.calibrated !== false) {
      throw new Error('Quality assessment must strictly be uncalibrated (calibrated: false)');
    }

    this.state.qualityAssessment = { ...qa };
    this.state.effectiveStatus = qa.status;
    this.state.overrideAudit = null;
  }

  /**
   * Explicit user override for advisory warnings.
   * Technical limits (hard bounds) CANNOT be overridden.
   */
  public overrideAssessment(reason: string): void {
    if (!reason || reason.trim().length === 0) {
      throw new Error('Override reason must not be empty');
    }

    if (this.state.qualityAssessment.isTechnicalLimit) {
      throw new Error('Cannot override technical hard limit (e.g. dimensions/bytes out of bounds)');
    }

    if (this.state.qualityAssessment.status === 'clean') {
      return;
    }

    this.state.effectiveStatus = 'proceed_with_warning';
    this.state.overrideAudit = {
      userOverridden: true,
      reason: reason.trim(),
      timestamp: new Date().toISOString(),
      originalStatus: this.state.qualityAssessment.status,
    };
  }

  /**
   * Update crop corners with geometric validation.
   */
  public setCropCorners(corners: CropCorners): void {
    if (!validateConvexCorners(corners)) {
      throw new Error('Degenerate or non-convex corners cannot form valid perspective crop');
    }

    const ordered = orderCorners(corners);
    this.state.cropCorners = ordered;
    this.state.isCropAdjusted = true;
  }

  /**
   * Rotate orientation clockwise by 90 degrees.
   */
  public rotateClockwise(): void {
    this.state.rotation = ((this.state.rotation + 90) % 360) as 0 | 90 | 180 | 270;
  }

  /**
   * Export final payload ready for prepare pipeline.
   */
  public buildPreparePayload(): PreparePayload {
    if (!this.state.file) {
      throw new Error('No capture file in active session');
    }

    return {
      fileName: this.state.file.name,
      rotation: this.state.rotation,
      corners: this.state.cropCorners,
      effectiveStatus: this.state.effectiveStatus,
      overrideAudit: this.state.overrideAudit ? { ...this.state.overrideAudit } : null,
    };
  }

  public getState(): Readonly<CaptureSessionState> {
    return {
      ...this.state,
      rawDimensions: { ...this.state.rawDimensions },
      previewDimensions: { ...this.state.previewDimensions },
      cropCorners: [...this.state.cropCorners.map((c) => [c[0], c[1]] as [number, number])] as CropCorners,
      qualityAssessment: {
        ...this.state.qualityAssessment,
        metrics: { ...this.state.qualityAssessment.metrics },
        reasons: [...this.state.qualityAssessment.reasons],
      },
      overrideAudit: this.state.overrideAudit ? { ...this.state.overrideAudit } : null,
    };
  }
}
